import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidePanel, SidePanelFooter } from './SidePanel';
import { Select } from './FormField';
import { Button } from '../ui/Button';
import { FormMultiSelect, FormMultiSelectOption } from '../ui/FormMultiSelect';
import { AuditLogFeed, AuditLogEntry } from '../ui/AuditLogFeed';
import { CommentsTab } from '../comments';
import { DependenciesTab } from '../dependencies/DependenciesTab';
import {
  initiativeApi,
  commentApi,
  productApi,
  subProductApi,
  classificationApi,
  statusApi,
  userApi,
  tagApi,
  fiscalYearApi,
  fieldConfigApi,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFieldConfig } from '../../hooks/useFieldConfig';
import { useLabsFeatures } from '../../hooks/useLabsFeatures';
import { useAppStore } from '../../stores/appStore';
import { JiraPushModal } from './JiraPushModal';
import { formatFiscalQuarterHint } from '../../utils/dateRange';

type TabId = 'main' | 'dependencies' | 'comments' | 'audit';

interface Initiative {
  id: string;
  title: string;
  subProductId: string;
  classificationId?: string;
  statusId: string;
  userId?: string;
  startQuarter: string | null;
  endQuarter?: string;
  tags?: Array<{ tagId: string }>;
  description?: string;
  isStarred?: boolean;
  notes?: string;
}

interface InitiativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initiative?: Initiative | null;
  onSuccess: () => void;
}

export function InitiativeModal({
  isOpen,
  onClose,
  initiative,
  onSuccess,
}: InitiativeModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fc = useFieldConfig();
  const labs = useLabsFeatures();
  const setStoreFieldConfigs = useAppStore((s) => s.setFieldConfigs);
  const triggerRefresh = useAppStore((s) => s.triggerRefresh);
  const [formData, setFormData] = useState({
    title: '',
    productId: '',
    subProductId: '',
    classificationId: '',
    statusId: '',
    userId: '',
    startQuarter: '',
    endQuarter: '',
    tagIds: [] as string[],
    description: '',
    isStarred: false,
    startDate: '',
    endDate: '',
    datePrecision: 'month' as 'month' | 'day',
  });
  const [commentCount, setCommentCount] = useState(0);
  const [dependencyCount, setDependencyCount] = useState(0);

  const [products, setProducts] = useState<any[]>([]);
  const [subProducts, setSubProducts] = useState<any[]>([]);
  const [classifications, setClassifications] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [quarters, setQuarters] = useState<Array<{ key: string; fullLabel: string; startDate: string; endDate: string }>>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('main');
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [jiraPushOpen, setJiraPushOpen] = useState(false);

  const isEditMode = Boolean(initiative);

  // Reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('main');
      setAuditLogs([]);
    }
  }, [isOpen]);

  // Reset dependency count when modal opens
  useEffect(() => {
    if (isOpen && initiative) {
      const activeDeps = (initiative as any).dependencies?.filter((d: any) => d.isActive !== false) || [];
      setDependencyCount(activeDeps.length);
    } else {
      setDependencyCount(0);
    }
  }, [isOpen, initiative?.id]);

  // Fetch comment count when modal opens (edit mode only)
  useEffect(() => {
    if (isOpen && initiative?.id) {
      commentApi.getCount(initiative.id)
        .then(({ count }) => setCommentCount(count))
        .catch(() => setCommentCount(0));
    } else {
      setCommentCount(0);
    }
  }, [isOpen, initiative?.id]);

  // Tab definitions with dynamic counts
  const TABS: { id: TabId; label: string | React.ReactNode; editOnly?: boolean; hidden?: boolean }[] = [
    { id: 'main', label: 'Main' },
    {
      id: 'dependencies',
      label: (
        <span className="inline-flex items-center gap-1.5">
          {fc.label('dependencies')}
          {dependencyCount > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {dependencyCount}
            </span>
          )}
        </span>
      ),
      editOnly: true,
      hidden: !labs.isEnabled('dependencies'),
    },
    {
      id: 'comments',
      label: (
        <span className="inline-flex items-center gap-1.5">
          Comments
          {commentCount > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {commentCount}
            </span>
          )}
        </span>
      ),
      editOnly: true,
    },
    { id: 'audit', label: 'Audit Log', editOnly: true },
  ];

  // Load audit logs when audit tab is selected
  useEffect(() => {
    const loadAuditLogs = async () => {
      if (activeTab === 'audit' && initiative?.id) {
        setAuditLoading(true);
        try {
          const logs = await initiativeApi.getAuditLog(initiative.id);
          setAuditLogs(logs);
        } catch (error) {
          console.error('Failed to load audit logs:', error);
        } finally {
          setAuditLoading(false);
        }
      }
    };

    loadAuditLogs();
  }, [activeTab, initiative?.id]);

  // Load all required data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [
          productsData,
          classificationsData,
          statusesData,
          usersData,
          tagsData,
          fiscalYearsData,
          fieldConfigData,
        ] = await Promise.all([
          productApi.getAll(),
          classificationApi.getAll(),
          statusApi.getAll(),
          userApi.getAll(),
          tagApi.getAll(),
          fiscalYearApi.getAll(),
          fieldConfigApi.getAll().catch(() => []),
        ]);

        setProducts(productsData);
        setClassifications(classificationsData);
        setStatuses(statusesData);
        setUsers(usersData);
        setTags(tagsData);
        if (fieldConfigData.length > 0) {
          setStoreFieldConfigs(fieldConfigData);
        }

        // Extract quarters from fiscal years (with fullLabel + date range for display)
        const allQuarters: Array<{ key: string; fullLabel: string; startDate: string; endDate: string }> = [];
        fiscalYearsData.forEach((fy: any) => {
          if (fy.quarters) {
            allQuarters.push(...fy.quarters.map((q: any) => ({ key: q.key, fullLabel: q.fullLabel, startDate: q.startDate, endDate: q.endDate })));
          }
        });
        setQuarters(allQuarters);
      } catch (error) {
        console.error('Failed to load data:', error);
        setErrors({
          submit: 'Failed to load form data',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Load sub-products when product changes
  useEffect(() => {
    const loadSubProducts = async () => {
      if (formData.productId) {
        try {
          const data = await subProductApi.getByProduct(formData.productId);
          setSubProducts(data);
        } catch (error) {
          console.error('Failed to load sub-products:', error);
        }
      } else {
        setSubProducts([]);
      }
    };

    loadSubProducts();
  }, [formData.productId]);

  // Populate form when editing - simple approach using data already in initiative prop
  useEffect(() => {
    if (!isOpen) return;
    if (isLoading) return;

    if (initiative) {
      // Get productId from the included subProduct relation
      // subProduct.productId is the FK column; subProduct.product.id is from the include
      const sub = (initiative as any).subProduct;
      const productId = sub?.productId || sub?.product?.id || '';

      setFormData({
        title: initiative.title || '',
        productId,
        subProductId: initiative.subProductId || '',
        classificationId: initiative.classificationId || '',
        statusId: initiative.statusId || '',
        userId: initiative.userId || '',
        startQuarter: initiative.startQuarter || '',
        endQuarter: initiative.endQuarter || '',
        tagIds: initiative.tags?.map((t) => t.tagId) || [],
        description: (initiative as any).description || '',
        isStarred: (initiative as any).isStarred || false,
        startDate: (initiative as any).startDate ? new Date((initiative as any).startDate).toISOString().split('T')[0] : '',
        endDate: (initiative as any).endDate ? new Date((initiative as any).endDate).toISOString().split('T')[0] : '',
        datePrecision: (initiative as any).datePrecision || 'month',
      });
    } else {
      // Reset form for new initiative
      const initialStatus = statuses.find((s: any) => s.isInitialStatus || s.isDefault);
      setFormData({
        title: '',
        productId: '',
        subProductId: '',
        classificationId: '',
        statusId: initialStatus?.id || '',
        userId: '',
        startQuarter: '',
        endQuarter: '',
        tagIds: [],
        description: '',
        isStarred: false,
        startDate: '',
        endDate: '',
        datePrecision: 'month' as 'month' | 'day',
      });
    }
    setErrors({});
  }, [initiative, isOpen, isLoading, statuses]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    } else if (formData.title.trim().length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (!formData.subProductId) {
      newErrors.subProductId = `${fc.label('subProduct')} is required`;
    }

    if (fc.isVisible('classification') && fc.isRequired('classification') && !formData.classificationId) {
      newErrors.classificationId = `${fc.label('classification')} is required`;
    }

    if (!formData.statusId) {
      newErrors.statusId = `${fc.label('status')} is required`;
    }

    if (fc.isRequired('owner') && !formData.userId) {
      newErrors.userId = `${fc.label('owner')} is required`;
    }

    if (formData.datePrecision === 'month') {
      if (!formData.startQuarter) {
        newErrors.startQuarter = `${fc.label('start')} period is required`;
      }

      if (fc.isVisible('end') && fc.isRequired('end') && !formData.endQuarter) {
        newErrors.endQuarter = `${fc.label('end')} period is required`;
      }

      if (formData.endQuarter && formData.startQuarter) {
        if (formData.endQuarter < formData.startQuarter) {
          newErrors.endQuarter = 'End must be after start';
        }
      }
    } else {
      if (!formData.startDate) {
        newErrors.startDate = `${fc.label('start')} date is required`;
      }
      if (formData.datePrecision === 'day' && formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Shared save logic — returns true on success, false on validation/API error.
  // keepOpen: when true, only triggers a list refresh without calling onSuccess()
  // (which would close the modal via App.tsx handleModalSuccess).
  const submitForm = async (keepOpen = false): Promise<boolean> => {
    if (!validate()) return false;

    setIsSubmitting(true);
    try {
      const data: Record<string, any> = {
        title: formData.title.trim(),
        subProductId: formData.subProductId,
        classificationId: fc.isVisible('classification') ? formData.classificationId || null : null,
        statusId: formData.statusId,
        userId: formData.userId || null,
        startQuarter: formData.datePrecision === 'month' ? formData.startQuarter : null,
        endQuarter: formData.datePrecision === 'month' ? (fc.isVisible('end') ? formData.endQuarter || null : null) : null,
        createdBy: user?.id,
        lastModifiedBy: user?.id,
        tags: fc.isVisible('tags') ? formData.tagIds : [],
        description: formData.description || null,
        isStarred: formData.isStarred,
        startDate: formData.datePrecision === 'day' ? (formData.startDate || null) : null,
        endDate: formData.datePrecision === 'day' ? (formData.endDate || null) : null,
        datePrecision: formData.datePrecision,
      };

      if (isEditMode && initiative) {
        await initiativeApi.update(initiative.id, data);
      } else {
        await initiativeApi.create(data);
      }

      if (keepOpen) {
        triggerRefresh(); // refresh list without closing the modal
      } else {
        onSuccess(); // triggerRefresh + close (via App.tsx handleModalSuccess)
      }
      return true;
    } catch (error) {
      console.error('Failed to save initiative:', error);
      setErrors({ submit: 'Failed to save initiative. Please try again.' });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await submitForm();
    if (ok) onClose();
  };

  const handleSaveAndAddAnother = async () => {
    const ok = await submitForm(true); // keepOpen = true
    if (ok) {
      setFormData((prev) => ({ ...prev, title: '' }));
      setErrors({});
      setActiveTab('main');
    }
  };

  const handleDelete = async () => {
    if (!initiative) return;

    if (!confirm(`Are you sure you want to delete "${initiative.title}"?`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await initiativeApi.delete(initiative.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete initiative:', error);
      setErrors({
        submit:
          error instanceof Error ? error.message : 'Failed to delete initiative',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const productOptions = products.map((p) => ({ value: p.id, label: p.name }));
  const subProductOptions = subProducts.map((sp) => ({ value: sp.id, label: sp.name }));
  const classificationOptions = classifications.map((c) => ({ value: c.id, label: c.name }));
  const statusOptions = statuses.map((s) => ({ value: s.id, label: s.name }));
  const userOptions = users.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }));
  const quarterOptions = quarters.map((q) => ({ value: q.key, label: q.fullLabel }));

  // Tag options for multi-select
  const tagOptions: FormMultiSelectOption[] = tags.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  // Filter tabs based on edit mode and field config
  const visibleTabs = TABS.filter((tab) => (!tab.editOnly || isEditMode) && !tab.hidden);

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? `Edit ${fc.label('initiative')}` : `Add New ${fc.label('initiative')}`}
      headerActions={isEditMode ? (
        <>
          <button
            type="button"
            onClick={() => navigate(`/initiatives/${initiative!.id}`)}
            className="rounded-md p-1 text-text-secondary hover:bg-surface-hover hover:text-blue-600 transition-colors"
            title="Open full page"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setFormData((prev) => ({ ...prev, isStarred: !prev.isStarred }))}
            className={`rounded-md p-1 transition-colors ${formData.isStarred ? 'text-amber-400 hover:bg-surface-hover' : 'text-text-secondary hover:bg-surface-hover hover:text-amber-400'}`}
            title={formData.isStarred ? 'Remove star' : 'Star this initiative'}
          >
            <svg className="w-4 h-4" fill={formData.isStarred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.95-.69l1.519-4.674z" />
            </svg>
          </button>
        </>
      ) : undefined}
    >
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center text-text-secondary">
          Loading form data...
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Tab Bar - pinned below header */}
          <div className="flex border-b border-gray-200 px-6 flex-shrink-0 overflow-x-auto scrollbar-hide">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-text-secondary hover:text-text-primary hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Scrollable tab content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Main Tab */}
            {activeTab === 'main' && (
              <div className="space-y-4">
                {/* Title field with character counter */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-text-primary"
                  >
                    Title
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Implement real-time payment processing"
                    maxLength={200}
                    className={`w-full rounded-md border px-3 py-2 text-sm bg-surface-default text-text-primary placeholder:text-text-tertiary transition-colors focus:ring-2 ${
                      formData.title.length >= 200
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : errors.title
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-border-default focus:border-border-focus focus:ring-border-focus/20'
                    }`}
                  />
                  <div className="flex justify-between items-center">
                    {errors.title ? (
                      <p className="text-sm text-red-500">{errors.title}</p>
                    ) : (
                      <span />
                    )}
                    <span
                      className={`text-xs ${
                        formData.title.length >= 200
                          ? 'text-red-500 font-medium'
                          : formData.title.length >= 180
                          ? 'text-orange-500'
                          : 'text-text-tertiary'
                      }`}
                    >
                      {formData.title.length} / 200
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label={fc.label('product')}
                    name="productId"
                    value={formData.productId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        productId: e.target.value,
                        subProductId: '',
                      })
                    }
                    options={productOptions}
                    placeholder={`Select a ${fc.label('product').toLowerCase()}`}
                    required
                    error={errors.productId}
                  />

                  <Select
                    label={fc.label('subProduct')}
                    name="subProductId"
                    value={formData.subProductId}
                    onChange={(e) => setFormData({ ...formData, subProductId: e.target.value })}
                    options={subProductOptions}
                    placeholder={`Select a ${fc.label('subProduct').toLowerCase()}`}
                    required
                    error={errors.subProductId}
                    disabled={!formData.productId}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {fc.isVisible('classification') && (
                    <Select
                      label={fc.label('classification')}
                      name="classificationId"
                      value={formData.classificationId}
                      onChange={(e) => setFormData({ ...formData, classificationId: e.target.value })}
                      options={classificationOptions}
                      placeholder={`Select a ${fc.label('classification').toLowerCase()}`}
                      required={fc.isRequired('classification')}
                      error={errors.classificationId}
                    />
                  )}

                  <Select
                    label={fc.label('status')}
                    name="statusId"
                    value={formData.statusId}
                    onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
                    options={statusOptions}
                    placeholder={`Select a ${fc.label('status').toLowerCase()}`}
                    required
                    error={errors.statusId}
                  />
                </div>

                <Select
                  label={fc.label('owner')}
                  name="userId"
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  options={userOptions}
                  placeholder={`Select an ${fc.label('owner').toLowerCase()}`}
                  required={fc.isRequired('owner')}
                  error={errors.userId}
                />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text-primary">Schedule</span>
                    <div className="flex rounded-md border border-border-default overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, datePrecision: 'month' })}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          formData.datePrecision === 'month'
                            ? 'bg-blue-600 text-white'
                            : 'bg-surface-default text-text-secondary hover:bg-surface-hover'
                        }`}
                      >
                        Month
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, datePrecision: 'day' })}
                        className={`px-3 py-1 text-xs font-medium transition-colors border-l border-border-default ${
                          formData.datePrecision === 'day'
                            ? 'bg-blue-600 text-white'
                            : 'bg-surface-default text-text-secondary hover:bg-surface-hover'
                        }`}
                      >
                        Date
                      </button>
                    </div>
                  </div>

                  {formData.datePrecision === 'month' && (
                    <div className="grid grid-cols-2 gap-4">
                      <Select
                        label={fc.label('start')}
                        name="startQuarter"
                        value={formData.startQuarter}
                        onChange={(e) => setFormData({ ...formData, startQuarter: e.target.value })}
                        options={quarterOptions}
                        placeholder={`Select ${fc.label('start').toLowerCase()} period`}
                        required
                        error={errors.startQuarter}
                      />

                      {fc.isVisible('end') && (
                        <Select
                          label={fc.label('end')}
                          name="endQuarter"
                          value={formData.endQuarter}
                          onChange={(e) => setFormData({ ...formData, endQuarter: e.target.value })}
                          options={quarterOptions}
                          placeholder={`Select ${fc.label('end').toLowerCase()} period`}
                          required={fc.isRequired('end')}
                          error={errors.endQuarter}
                          helpText={!fc.isRequired('end') ? 'Optional' : undefined}
                        />
                      )}
                    </div>
                  )}

                  {formData.datePrecision === 'day' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label htmlFor="startDate" className="block text-sm font-medium text-text-primary">
                          {fc.label('start')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="startDate"
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                          className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-surface-default text-text-primary focus:ring-2 focus:ring-border-focus/20 focus:border-border-focus focus:outline-none"
                        />
                        {formData.startDate && (
                          <p className="text-xs text-text-tertiary">{formatFiscalQuarterHint(formData.startDate, quarters)}</p>
                        )}
                        {errors.startDate && <p className="text-sm text-red-500">{errors.startDate}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="endDate" className="block text-sm font-medium text-text-primary">
                          {fc.label('end')}
                        </label>
                        <input
                          id="endDate"
                          type="date"
                          value={formData.endDate}
                          min={formData.startDate || undefined}
                          onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                          className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-surface-default text-text-primary focus:ring-2 focus:ring-border-focus/20 focus:border-border-focus focus:outline-none"
                        />
                        {formData.endDate && (
                          <p className="text-xs text-text-tertiary">{formatFiscalQuarterHint(formData.endDate, quarters)}</p>
                        )}
                        {errors.endDate && <p className="text-sm text-red-500">{errors.endDate}</p>}
                      </div>
                    </div>
                  )}
                </div>

                {fc.isVisible('tags') && tagOptions.length > 0 && (
                  <FormMultiSelect
                    label={fc.label('tags')}
                    options={tagOptions}
                    selected={formData.tagIds}
                    onChange={(selected) => setFormData({ ...formData, tagIds: selected })}
                    placeholder={`Select ${fc.label('tags').toLowerCase()}...`}
                    helpText="Optional"
                    searchable
                  />
                )}

                <div className="space-y-1.5">
                  <label htmlFor="description" className="block text-sm font-medium text-text-primary">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Short description (optional)"
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <div className="flex justify-end">
                    <span
                      className={`text-xs ${
                        formData.description.length >= 500
                          ? 'text-red-500 font-medium'
                          : 'text-text-tertiary'
                      }`}
                    >
                      {formData.description.length} / 500
                    </span>
                  </div>
                </div>


              </div>
            )}

            {/* Dependencies Tab */}
            {activeTab === 'dependencies' && isEditMode && initiative && (
              <DependenciesTab
                initiativeId={initiative.id}
                onDependencyCountChange={setDependencyCount}
              />
            )}

            {/* Comments Tab */}
            {activeTab === 'comments' && isEditMode && initiative && (
              <div>
                <CommentsTab
                  initiativeId={initiative.id}
                  onCommentCountChange={setCommentCount}
                />
              </div>
            )}

            {/* Audit Log Tab */}
            {activeTab === 'audit' && isEditMode && (
              <div>
                <AuditLogFeed entries={auditLogs} loading={auditLoading} compact />
              </div>
            )}

            {errors.submit && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 mt-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Footer - pinned at bottom */}
          <SidePanelFooter>
            {isEditMode && (
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="mr-auto"
              >
                Delete
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            {!isEditMode && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleSaveAndAddAnother}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save & Add Another'}
              </Button>
            )}
            {isEditMode && labs.isEnabled('jira') && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setJiraPushOpen(true)}
                disabled={isSubmitting}
              >
                Push to Jira
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditMode ? 'Save' : 'Add'}
            </Button>
          </SidePanelFooter>
        </form>
      )}

      {jiraPushOpen && initiative && (
        <JiraPushModal
          isOpen={jiraPushOpen}
          onClose={() => setJiraPushOpen(false)}
          initiative={initiative as any}
          onSuccess={() => setJiraPushOpen(false)}
        />
      )}
    </SidePanel>
  );
}
